// src/components/shared/DataToolbar.js
// Toolbar canônico de páginas com listing: busca + filtros inline + extras +
// filter button. Substitui SearchFilterRow (que vira re-export por compat).
//
// Layout (2026-05-11, Fase 9):
//   - Width: 100% do AppContent (drop o 60% centralizado anterior).
//   - Row: [ Search | spacer | Filters inline | extraActions | Filter button ]
//   - Altura uniforme 40px (override do theme MuiOutlinedInput 44 via sx).
//   - Search: flex 1 + max-width 480px.
//   - Filters inline: width 200-240 cada (compacto).
//   - Mobile (xs/sm): direction column, todos 100% width.
//
// API:
//   search: { value, onChange, onClear?, onVoice?, placeholder?, autoFocus? }
//   filters: [{ id, options, value, onChange, label, getOptionLabel? }]
//   filterButton: { onClick, count?, label? }
//   extraActions: ReactNode (slot right, antes do filterButton)
//   alertSlot: ReactNode (renderizado ABAIXO da row)
//   preChildren: ReactNode (renderizado ACIMA da row — compat com IATF preHeader)
//                — DEPRECATED: prefer PageInfoStrip (Fase 10).

import React from 'react'
import { Box } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchBar from './SearchBar'
import Autocomplete from '../material/Autocomplete'
import AppButton from '../material/AppButton'

const TOOLBAR_FIELD_HEIGHT = 40

export default function DataToolbar({
  search,
  filters = [],
  filterButton,
  extraActions,
  alertSlot,
  preChildren,
  sx,
}) {
  const hasSearch = !!search
  const hasFilters = filters.length > 0
  const hasFilterButton = !!filterButton

  // sx override para todos os fields/buttons baterem 40px (theme global = 44).
  const fieldHeightOverride = {
    '& .MuiOutlinedInput-root': { minHeight: TOOLBAR_FIELD_HEIGHT },
    '& .MuiOutlinedInput-input': { py: 1 },
  }

  return (
    <Box
      component='section'
      aria-label='Busca e filtros'
      sx={{ width: '100%', mb: 2, ...sx }}
    >
      {preChildren && <Box sx={{ mb: 2 }}>{preChildren}</Box>}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        {hasSearch && (
          <Box
            sx={{
              flex: { md: 1 },
              maxWidth: { md: 480 },
              ...fieldHeightOverride,
            }}
          >
            <SearchBar
              value={search.value}
              onChange={search.onChange}
              onClear={search.onClear}
              onVoiceClick={search.onVoice}
              placeholder={search.placeholder}
              autoFocus={search.autoFocus}
            />
          </Box>
        )}

        {/* Spacer empurrando filtros pra direita em desktop */}
        <Box sx={{ flex: { md: 1 }, display: { xs: 'none', md: 'block' } }} />

        {hasFilters &&
          filters.map((f) => (
            <Box
              key={f.id}
              sx={{ width: { xs: '100%', md: 220 }, ...fieldHeightOverride }}
            >
              <Autocomplete
                id={f.id}
                options={f.options || []}
                value={f.value || null}
                onChange={(e, val) => f.onChange(val)}
                label={f.label}
                useDefaultOptionSelected
                getOptionLabel={
                  f.getOptionLabel ||
                  ((option) => {
                    if (option == null) return ''
                    if (typeof option === 'string') return option
                    return (
                      option.valor ||
                      option.label ||
                      option.name ||
                      option.descricao ||
                      ''
                    )
                  })
                }
              />
            </Box>
          ))}

        {extraActions}

        {hasFilterButton && (
          <AppButton
            variant='secondary'
            startIcon={<FilterListIcon />}
            onClick={filterButton.onClick}
            sx={{ flexShrink: 0 }}
          >
            {filterButton.label || 'Filtros'}
            {filterButton.count != null &&
              filterButton.count > 0 &&
              ` (${filterButton.count})`}
          </AppButton>
        )}
      </Box>

      {alertSlot && <Box sx={{ mt: 2 }}>{alertSlot}</Box>}
    </Box>
  )
}
